'use client';

import type {
  CategoryNavigationData,
  CategoryNavigationItem,
} from '@apis/renewal/categoryMain';
import { useMemo } from 'react';

/** 네비게이션 아이템 고유 값 (탭·딤레이어 value) — '전체' 항목이 상위와 같은 코드를 가져 이름까지 포함 */
export const getNavigationItemValue = (item: CategoryNavigationItem): string =>
  `${item.mediumCd}-${item.largeCategory}-${item.middleCategory}-${item.smallCategory}-${item.categoryName}`;

/** CategoryDimLayer 2단 목록 */
export interface DimLayerCategoryItem {
  text: string;
  value: string;
  categoryList?: DimLayerCategoryItem[];
}

/** 코드 배열 [몰, 대, 중, 소] */
const toCodes = (item: CategoryNavigationItem): string[] => [
  item.mediumCd,
  item.largeCategory,
  item.middleCategory,
  item.smallCategory,
];

/** 항목 레벨: 0 몰 / 1 대분류 / 2 중분류 / 3 소분류 — 대·중·소 중 처음 '000' 이 나오는 자리 */
const getLevel = (item: CategoryNavigationItem): number => {
  const index = toCodes(item).slice(1).indexOf('000');
  return index === -1 ? 3 : index;
};

/** 목록의 레벨 = 가장 깊은 항목 기준('전체' 항목은 한 단계 얕음) */
const getListLevel = (list: CategoryNavigationItem[]): number =>
  Math.max(...list.map(getLevel));

/**
 * 목록에서 현재 위치 항목 찾기.
 * 항목 레벨까지의 코드가 현재 위치와 모두 일치하는 것 중 가장 깊은 것 (같으면 하위 목록 있는 쪽 — '전체' 배제)
 */
const findCurrent = (
  list: CategoryNavigationItem[],
  position: string[]
): CategoryNavigationItem | undefined =>
  list
    .filter(item =>
      toCodes(item)
        .slice(0, getLevel(item) + 1)
        .every((code, index) => code === position[index])
    )
    .sort(
      (a, b) =>
        getLevel(b) - getLevel(a) ||
        (b.subCategory?.length ? 1 : 0) - (a.subCategory?.length ? 1 : 0)
    )[0];

/**
 * 네비게이션(v2/list) 응답 → 3·4뎁스 탭바 + 딤레이어 데이터. 코드 인자는 '000' 표기.
 * - 현재 항목: 바로가기 목록에서 findCurrent
 * - 탭줄: [바로가기 목록(다른 몰이 섞인 최상위 목록 제외), 현재 항목 하위, navigation 하위]
 *         를 레벨별로 합쳐 가장 깊은 두 레벨이 3·4뎁스
 * - 딤레이어: 왼쪽 = 바로가기 목록, 오른쪽 = 각 항목 하위 (현재 항목은 하위 없으면 navigation 하위)
 */
export function useCategoryNavigationTabs({
  navigation,
  mediumCd,
  largeCategory,
  middleCategory,
  smallCategory,
}: {
  navigation: CategoryNavigationData | undefined;
  mediumCd: string;
  largeCategory: string;
  middleCategory: string;
  smallCategory: string;
}): {
  /** 바로가기 목록(displayOrder 정렬) */
  shortcutList: CategoryNavigationItem[];
  depth3List: CategoryNavigationItem[];
  depth4List: CategoryNavigationItem[];
  selectedDepth3: CategoryNavigationItem | undefined;
  selectedDepth4: CategoryNavigationItem | undefined;
  dimLayerCategoryList: DimLayerCategoryItem[];
  /** 딤레이어 왼쪽/오른쪽 활성 value */
  dimLayerActiveValue: string | undefined;
  dimLayerActiveValueDepth2: string | undefined;
  /** value → 원본 네비게이션 아이템 */
  findNavigationItemByValue: (
    value: string
  ) => CategoryNavigationItem | undefined;
} {
  return useMemo(() => {
    const position = [mediumCd, largeCategory, middleCategory, smallCategory];
    const navigationSubList = navigation?.subCategory ?? [];

    // 1) 바로가기 목록 + 현재 항목 + 현재 항목의 하위 목록
    const shortcutList = [...(navigation?.shortcutCategory ?? [])].sort(
      (a, b) => a.displayOrder - b.displayOrder
    );
    const current = findCurrent(shortcutList, position);
    const currentSubList = current?.subCategory?.length
      ? current.subCategory
      : navigationSubList;

    // 2) 탭줄: 후보를 레벨별로 합친 뒤 가장 깊은 두 레벨
    const isMixedMall = shortcutList.some(item => item.mediumCd !== mediumCd);
    const listByLevel = new Map<number, CategoryNavigationItem[]>();
    [isMixedMall ? [] : shortcutList, currentSubList, navigationSubList]
      .filter(list => list.length > 0)
      .forEach(list => listByLevel.set(getListLevel(list), list));
    const rows = [...listByLevel.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, list]) => list)
      .slice(-2);
    const [depth3List, depth4List] =
      rows.length === 2 ? rows : [rows[0] ?? [], []];

    // 3) 딤레이어
    const dimLayerCategoryList: DimLayerCategoryItem[] = shortcutList.map(
      shortcut => {
        const subList =
          shortcut === current ? currentSubList : shortcut.subCategory;
        return {
          text: shortcut.categoryName,
          value: getNavigationItemValue(shortcut),
          categoryList: subList?.map(sub => ({
            text: sub.categoryName,
            value: getNavigationItemValue(sub),
          })),
        };
      }
    );
    const currentSub = findCurrent(currentSubList, position);

    const allItems = [
      ...shortcutList,
      ...shortcutList.flatMap(shortcut => shortcut.subCategory ?? []),
      ...navigationSubList,
    ];

    return {
      shortcutList,
      depth3List,
      depth4List,
      selectedDepth3: findCurrent(depth3List, position),
      selectedDepth4: findCurrent(depth4List, position),
      dimLayerCategoryList,
      dimLayerActiveValue: current && getNavigationItemValue(current),
      dimLayerActiveValueDepth2:
        currentSub && getNavigationItemValue(currentSub),
      findNavigationItemByValue: (value: string) =>
        allItems.find(item => getNavigationItemValue(item) === value),
    };
  }, [navigation, mediumCd, largeCategory, middleCategory, smallCategory]);
}
